import { useParams } from 'react-router-dom';
import {
  Stack,
  Image,
  If,
  P,
  Icon,
  ReadOnlyField,
} from '@fashionnovateam/nova-ui-toolkit';
import ItemDetailsForm from './ItemDetailsForm';
import { useGetVendorItem } from '../../../Lib/Hooks/ItemService/useGetVendorItem';
import ItemDetailsReadOnly from './ItemDetailsReadOnly';
import { ItemFilters } from '../../../Lib/Hooks/ItemService';
import EmptyStateLayout from './EmptyStateLayout';
import { ItemKeyDetails } from './ItemKeyDetails';
import { useGetItemPhotoURLs } from '../../../Lib/Hooks/ItemService/useGetItemPhotoURLs';

function ItemDetailsPage() {
  const { id: currentUserId, itemId: currentItemId } = useParams<{
    id: string;
    itemId: string;
  }>();

  const { data: targetItem } = useGetVendorItem(currentUserId, currentItemId);
  const { data: photoUrls, isLoading: isPhotosLoading } = useGetItemPhotoURLs(
    currentUserId,
    currentItemId,
  );

  const mode =
    targetItem?.stage === ItemFilters.PENDING_VENDOR ? 'edit' : 'read';

  const visiblePhotos = photoUrls?.gallery_photos ?? [];

  const renderPhotoGallery = () => {
    if (isPhotosLoading) {
      return (
        <div className="aspect-[2/3] bg-neutral-200 flex items-center justify-center">
          <Icon.LoadingCircle />
        </div>
      );
    }

    if (!targetItem) {
      return null;
    }

    if (visiblePhotos.length > 0) {
      return visiblePhotos.map((photo) => (
        <Image
          key={photo.url}
          className="aspect-[2/3] object-cover bg-neutral-200"
          src={photo.url}
          alt={`${photo.type} photo`}
          loading="lazy"
          decoding="async"
        />
      ));
    }

    return (
      <div className="flex flex-1 items-center justify-center">
        <P size="xs" color="disabled">
          No photos available
        </P>
      </div>
    );
  };

  return (
    <Stack direction="row" gap="sm">
      <Stack
        direction="col"
        gap="xs"
        className="w-1/4 bg-neutral-200 overflow-auto h-full"
      >
        {renderPhotoGallery()}
      </Stack>
      <Stack direction="col" gap="md" className="w-3/4 overflow-auto p-4">
        <div className="shadow rounded">
          <ItemKeyDetails />
        </div>
        <If condition={mode === 'edit' && !!targetItem?.vendor_notes}>
          <div className="bg-neutral-100 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="chat/chat-default" variant="solid" />
              <P weight="medium">Notes</P>
            </div>
            <ReadOnlyField
              label="Vendor Notes"
              value={targetItem?.vendor_notes}
              id="vendor-notes"
            />
          </div>
        </If>
        <div className="shadow rounded w-full">
          <If condition={mode === 'read'}>
            {!targetItem ||
            targetItem?.stage === ItemFilters.NEEDS_REVIEW ||
            targetItem?.stage === ItemFilters.ACCEPTED ? (
              <EmptyStateLayout />
            ) : (
              <ItemDetailsReadOnly />
            )}
          </If>
          <If condition={mode === 'edit'}>
            <ItemDetailsForm />
          </If>
        </div>
      </Stack>
    </Stack>
  );
}

export default ItemDetailsPage;
